import { useMemo } from "react"

import { useTranslation } from "react-i18next"

import { lightTheme } from "../../../../theme"
import { granularityTag } from "../../charts/axis"
import HorizontalBars from "../../charts/HorizontalBars"
import { SERIES_COLORS } from "../../charts/palette"
import TrendLineChart from "../../charts/TrendLineChart"
import ChangeBadge from "../../components/Badges/ChangeBadge"
import StatusBadge from "../../components/Badges/StatusBadge"
import InfoNote from "../../components/InfoNote/InfoNote"
import KpiStrip from "../../components/KpiStrip/KpiStrip"
import MetricCard from "../../components/MetricCard/MetricCard"
import { Panel, PanelRow } from "../../components/Panel/Panel"
import { useCardData } from "../../hooks/useCardData"
import { getPercentChange, GOOD_DIRECTION } from "../../utils/change"
import { formatCount } from "../../utils/formats"

// The card's Tooltip terms, verbatim from the spec (§4 Active merchants).
const INFO = [
  {
    name: "Active",
    formula: "Distinct merchants with at least one lookup in the period; each merchant counts once"
  },
  { name: "New", formula: "Merchants whose very first lookup in all history falls in the period" },
  { name: "Lost", formula: "Active in the comparison period, no lookup in the selected period" },
  {
    name: "Trend",
    formula: "Active and new, counted separately for each day, or each week beyond 90 days"
  },
  {
    name: "By program",
    formula: "Each active merchant falls into one group: CC only, OI only, or both"
  },
  {
    name: "Tip",
    formula:
      "Use a period of 7 days or more: on shorter periods merchants with rare lookups keep moving between lost and active"
  }
]

// The New bars: the platform's light bar blue, as its countries bars. The admin panel is light only.
const NEW_BAR_COLOR = "#93C1FD"

// Active merchants (DAA-4-07 … DAA-4-12): how many merchants actually receive lookups, how many are
// new and how many dropped off — the KPIs, the trend of active and new, daily up to 90 days as the
// page's other trends (CHA1-4558), and the split by program. The merchant tag filters do not reach it
// (DAA-2-27).
const ActiveMerchantsCard = () => {
  const { t } = useTranslation()
  const { data, loading } = useCardData("activeMerchants")
  const kpis = data?.kpis
  const comparison = data?.comparison

  const tiles = useMemo(
    () => [
      {
        key: "active",
        label: t("Active merchants"),
        value: kpis?.active,
        // A change badge only while comparing (DAA-2-22).
        badge: comparison ? (
          <ChangeBadge
            change={getPercentChange(kpis?.active, comparison.active, GOOD_DIRECTION.UP)}
          />
        ) : null,
        secondary: comparison ? `vs ${formatCount(comparison.active)} ${t("prev. period")}` : null
      },
      {
        key: "new",
        label: t("New merchants"),
        value: kpis?.new,
        secondary: t("first lookup ever in period · ignores program & channel")
      },
      {
        key: "lost",
        label: t("Lost merchants"),
        value: kpis?.lost,
        // A status, not a change: it stays with Do not compare (DAA-2-22).
        badge: kpis ? <StatusBadge status={kpis.lost > 0 ? "attention" : "none"} /> : null,
        secondary: t("active before, silent now")
      }
    ],
    [kpis, comparison, t]
  )

  const activeSeries = useMemo(
    () => [
      {
        key: "active",
        label: t("Active"),
        color: lightTheme.token.colorPrimary,
        data: data?.trend.active
      }
    ],
    [data, t]
  )
  const newBars = useMemo(
    () => [
      {
        key: "new",
        label: t("New"),
        color: NEW_BAR_COLOR,
        data: data?.trend.new
      }
    ],
    [data, t]
  )

  const programRows = useMemo(() => {
    const counts = data?.by_program

    // No active merchant: the panel reads "No data available", as the page's other empty panels.
    if (!counts || !(counts.cc_only + counts.oi_only + counts.cc_and_oi)) return []

    return [
      { key: "cc_only", label: t("CC only"), color: SERIES_COLORS.cc, value: counts.cc_only },
      { key: "oi_only", label: t("OI only"), color: SERIES_COLORS.oi, value: counts.oi_only },
      {
        key: "cc_and_oi",
        label: t("CC + OI"),
        // The platform's orange, apart from both program colours.
        color: lightTheme.token.colorWarning,
        value: counts.cc_and_oi
      }
    ]
  }, [data, t])

  return (
    <MetricCard
      title={t("Active merchants")}
      info={INFO}
      notAffectedBy={t("Not affected by: User email, User full name, Business account")}
      loading={loading}
    >
      <KpiStrip tiles={tiles} />
      {/* A full-width card (CHA1-4558): By program beside the trend, under the second KPI divider
          (DAA-1-21). */}
      <PanelRow>
        <Panel title={t("Trend")} tag={data && t(granularityTag(data.granularity))} span={8}>
          {/* `legend` even with one line: the New bars need their entry too (DAA-4-10). */}
          <div style={{ height: 260 }}>
            {data && (
              <TrendLineChart
                labels={data.labels}
                series={activeSeries}
                bars={newBars}
                lastIntervalIncomplete={data.last_interval_incomplete}
                fill
                legend
              />
            )}
          </div>
        </Panel>
        <Panel title={t("By program")} tag={t("ignores program filter")} span={4}>
          {data && <HorizontalBars rows={programRows} />}
          <InfoNote>
            {t(
              "Previous active + new − lost ≠ current active: merchants returning after a pause count in active only."
            )}
          </InfoNote>
        </Panel>
      </PanelRow>
    </MetricCard>
  )
}

export default ActiveMerchantsCard
